import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ImageUpload } from "@/components/admin/image-upload";
import { useHeroContent } from "@/hooks/use-supabase-data";
import {
  Camera,
  CheckCircle2,
  Image as ImageIcon,
  RefreshCw,
  Save,
  AlertCircle,
} from "lucide-react";

const defaultFormState = {
  title: "",
  subtitle: "",
  background_image_url: "",
};

export function HeroManager() {
  const { heroContent, loading, error, refetch } = useHeroContent();
  const [formState, setFormState] = useState(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (heroContent) {
      setFormState({
        title: heroContent.title ?? "",
        subtitle: heroContent.subtitle ?? "",
        background_image_url: heroContent.background_image_url ?? "",
      });
    } else {
      setFormState(defaultFormState);
    }
  }, [heroContent]);

  const handleInputChange =
    (field: keyof typeof formState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormState((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleImageChange = (imageUrl: string) => {
    setFormState((prev) => ({
      ...prev,
      background_image_url: imageUrl,
    }));
  };

  const handleRefresh = async () => {
    setSuccess("");
    setFormError("");
    await refetch();
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setSuccess("");
    setFormError("");

    const payload = {
      title: formState.title.trim(),
      subtitle: formState.subtitle.trim(),
      background_image_url: formState.background_image_url || null,
      updated_at: new Date().toISOString(),
    };

    if (!payload.title) {
      setFormError("Title is required.");
      setSaving(false);
      return;
    }

    if (!payload.subtitle) {
      setFormError("Subtitle is required.");
      setSaving(false);
      return;
    }

    try {
      if (heroContent?.id) {
        const { error } = await supabase
          .from("hero_content")
          .update(payload)
          .eq("id", heroContent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("hero_content").insert({
          ...payload,
          created_at: new Date().toISOString(),
        });

        if (error) throw error;
      }

      setSuccess("Hero section updated successfully.");
      await refetch();
    } catch (_err) {
      setFormError("Unable to save hero content. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
            Hero Section
          </h2>
          <p className="text-slate-600 text-lg">
            Control the homepage headline, description, and background image.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || saving}
            className="w-full sm:w-auto hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {formError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-rose-200 bg-rose-50 text-rose-700">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Homepage Hero Content
          </CardTitle>
          <CardDescription>
            Update the primary village branding shown on the homepage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
              <Camera className="h-8 w-8 animate-pulse" />
              <p>Loading hero content...</p>
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="hero-title">Hero Title</Label>
                <Input
                  id="hero-title"
                  value={formState.title}
                  onChange={handleInputChange("title")}
                  placeholder="e.g., Sandalpur Village"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hero-subtitle">Hero Subtitle</Label>
                <Textarea
                  id="hero-subtitle"
                  rows={3}
                  value={formState.subtitle}
                  onChange={handleInputChange("subtitle")}
                  placeholder="A thriving community committed to ..."
                />
              </div>

              <ImageUpload
                currentImage={formState.background_image_url}
                onImageChange={handleImageChange}
                folder="hero"
                label="Background Image"
                description="Upload a wide banner image for the hero section."
              />

              <div className="flex justify-end gap-2">
                <Button type="submit" disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

